import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' = red confirm button (logout, cancel bet), 'primary' = green */
  variant?: 'danger' | 'primary';
  /** Show site logo at top of modal (default true) */
  showLogo?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'primary',
  showLogo = true,
}) => {
  const { t } = useTranslation();
  useBodyScrollLock(isOpen);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isDanger = variant === 'danger';

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-desc"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-md bg-dark-800 border border-white/10 rounded-2xl shadow-2xl shadow-black/40 animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {showLogo && (
          <div className="pt-6 px-6 flex justify-center">
            <img
              src="/optimized/main-logo.webp"
              alt="ApexBet Logo"
              className="h-auto w-auto max-h-10 object-contain"
            />
          </div>
        )}
        {/* Icon + Title block */}
        <div className={`px-6 pb-4 ${showLogo ? 'pt-4' : 'pt-8'}`}>
          <div
            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full mb-5 ${
              isDanger
                ? 'bg-red-500/15 text-red-400'
                : 'bg-primary-500/15 text-primary-400'
            }`}
          >
            {isDanger ? (
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <h3 id="confirm-modal-title" className="text-xl font-semibold text-white text-center">
            {title}
          </h3>
        </div>

        {/* Message */}
        <p id="confirm-modal-desc" className="px-6 pb-6 text-center text-gray-400 text-[15px] leading-relaxed">
          {message}
        </p>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-medium text-gray-300 bg-dark-600/80 hover:bg-dark-500 border border-white/5 hover:border-white/10 transition-all duration-200"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={`flex-1 py-3 rounded-xl font-semibold text-white transition-all duration-200 ${
              isDanger
                ? 'bg-red-600 hover:bg-red-500 shadow-lg shadow-red-500/25 hover:shadow-red-500/30'
                : 'bg-primary-600 hover:bg-primary-500 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/30'
            }`}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
